import { Feature, Map } from "ol";
import { transform, transformExtent } from "ol/proj";
import View from "ol/View";
import { defaults } from "ol/control";
import { defaults as interactionDefaults } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Fill, Icon, Stroke, Style } from "ol/style";
import * as mapSettings from "../assets/map.json";
import { GeoJSON, MVT } from "ol/format";
import { Geometry } from "ol/geom";
import CircleStyle from "ol/style/Circle";
import VectorTileLayer from "ol/layer/VectorTile";
import VectorTileSource from "ol/source/VectorTile";
import TileGrid from "ol/tilegrid/TileGrid";
import { applyStyle } from "ol-mapbox-style";
import { LineString, MultiLineString } from "ol/geom";
import { PARK_MACHINE_TYPE } from "../constants";

const { mapCenter, mapZoom, mapMaxExtent, maxZoom } = mapSettings;

const vectorTileServerUrl = "https://tiles.arcgis.com/tiles/SBTXIEUGWbqzUecw/arcgis/rest/services/zakladni_mapa/VectorTileServer";
// Oficiální styl IPR "Barevná" s našimi úpravami fontů a barev, generuje se přes `npm run map-style:update`
const mapStyleUrl = "/map-style/opa.json";

const getVectorTileSource = async (url: string) => {
    const response = await fetch(`${url}?f=json`);
    if (!response.ok) {
        throw new Error(`Failed to load vector tile service: ${response.status}`);
    }
    const { tileInfo, fullExtent, tiles } = await response.json();
    const resolutions: number[] = tileInfo.lods.map((lod: { resolution: number }) => lod.resolution);
    const source = new VectorTileSource({
        format: new MVT(),
        url: `${url}/${tiles[0]}`,
        projection: "EPSG:5514",
        attributions: "© IPR Praha",
        tileGrid: new TileGrid({
            origin: [tileInfo.origin.x, tileInfo.origin.y],
            extent: [fullExtent.xmin, fullExtent.ymin, fullExtent.xmax, fullExtent.ymax],
            resolutions,
            tileSize: tileInfo.rows,
        }),
    });
    return { source, resolutions };
};

const layer = new VectorTileLayer({ declutter: true, renderMode: "hybrid" });

export const map = new Map({
    target: "map",
    // @ts-ignore
    controls: defaults({ attribution: false, zoom: false }),
    interactions: interactionDefaults({
        pinchRotate: false,
        shiftDragZoom: false,
    }),
});

export const initMapLayer = async () => {
    const maxExtent = transformExtent(mapMaxExtent, "EPSG:4326", "EPSG:5514");
    const mapView = new View({
        center: transform(mapCenter, "EPSG:4326", "EPSG:5514"),
        zoom: mapZoom,
        extent: maxExtent,
        maxZoom: maxZoom,
        projection: "EPSG:5514",
    });
    map.setView(mapView);
    map.setLayers([layer, parkingZones, parkingMachines, myLocationMarker, locationMarker]);

    const { source, resolutions } = await getVectorTileSource(vectorTileServerUrl);
    await applyStyle(layer, mapStyleUrl, {
        resolutions,
        updateSource: false,
        webfonts: "data:text/css,",
    });
    layer.setSource(source);
}

const hexToRgb = (hex: string) =>
    hex
        ?.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => "#" + r + r + g + g + b + b)
        .substring(1)
        .match(/.{2}/g)
        .map((x) => parseInt(x, 16));

export const styleElement = (color: string, selected: boolean | null, hover: boolean = false) => {
    const rgb = hexToRgb(color);
    const fillOpacity = hover ? 0.5 : selected === false ? 0.3 : 0.8;

    return new Style({
        stroke: new Stroke({
            color: color,
            width: hover ? 1.5 : 1,
        }),
        fill: new Fill({
            color: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${fillOpacity})`,
        }),
    });
};

export const parkingMachineStyle = (color: string, feature: any) => {
    if (feature !== null && feature.getProperties().parkMachineType === PARK_MACHINE_TYPE.infobox) {
        return infoBoxStyle(color);
    }

    const iconStyle = new Style({
        image: new Icon({
            src: "/images/parking-machine.svg",
            scale: 1.1,
            anchor: [0.5, 0.5],
        }),
    });

    const circleStyle = new Style({
        image: new CircleStyle({
            radius: 15,
            fill: new Fill({
                color: color,
            }),
            /* stroke: new Stroke({
                color: 'rgb(255, 255, 255)',
                width: 2,
            })*/
        }),
    });

    return [circleStyle, iconStyle];
};

export const infoBoxStyle = (color: string) => {
    const iconStyle = new Style({
        image: new Icon({
            src: color === "#d40939" ? "/images/info-box-hover.svg" : "/images/info-box.svg",
            scale: 1.1,
            anchor: [0.5, 0.5],
        }),
    });

    const circleStyle = new Style({
        image: new CircleStyle({
            radius: 15,
            fill: new Fill({
                color: color,
            }),
            /*stroke: new Stroke({
                color: 'rgb(255, 255, 255)',
                width: 2,
            })*/
        }),
    });

    return [circleStyle, iconStyle];
};

export const invisibleStyle = new Style({
    fill: null,
    stroke: null,
});

export const parkingZones = new VectorLayer({
    source: new VectorSource(),
});

export const parkingMachines = new VectorLayer({
    source: new VectorSource(),
});

export const myLocationMarker = new VectorLayer({
    source: new VectorSource(),
});

export const locationMarker = new VectorLayer({
    source: new VectorSource(),
});

export const myLocationMarkerStyleBackground = new Style({
    fill: new Fill({
        color: "rgba(99, 147, 242, 0.2)",
    }),
    zIndex: 5,
});

export const myLocationMarkerStyleCircle = new Style({
    stroke: new Stroke({
        color: "#ffffff",
        width: 2,
    }),
    fill: new Fill({
        color: "#6393F2",
    }),
    zIndex: 10,
});

export const loadParkingZonesIntoMap = (data: any, isFirstLoad: boolean): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        try {
            data.forEach((zone) => {
                if (!parkingZones.getSource().getFeatureById(zone.sectionID)) {
                    let features = new GeoJSON().readFeatures(zone.sectionBorder, {
                        dataProjection: "EPSG:4326",
                        featureProjection: "EPSG:5514",
                    });

                    features.forEach((feature) => {
                        if (feature instanceof Feature) {
                            feature.setStyle(invisibleStyle);
                            feature.setId(zone.sectionID);
                            feature.setProperties({
                                ...zone,
                                streetName: zone?.streetName?.toUpperCase(),
                                featureType: "parkingZone",
                                visible: false,
                            });
                        }
                    });

                    parkingZones.getSource().addFeatures(features as Feature<Geometry>[]);
                }
            });

            resolve();
        } catch (error) {
            reject(error);
        }
    });
};

export const addParkingMachine = (geojson: any, color: string, properties: any, isFirstLoad: boolean) => {
    if (parkingMachines.getSource()?.getFeatureById(properties.parkMachineID)) {
        return;
    }

    const format = new GeoJSON();
    const features = format.readFeatures(geojson, {
        featureProjection: "EPSG:5514",
    });

    features.forEach((feature) => {
        if (feature instanceof Feature) {
            feature.setProperties({
                ...properties,
                streetName: properties?.streetName?.toUpperCase(),
                featureType: "parkingMachine",
                visible: false,
            });

            feature.setStyle(invisibleStyle);

            feature.setId(properties.parkMachineID);

            parkingMachines.getSource()?.addFeature(feature as Feature<Geometry>);
        }
    });
};

// Batch verzia - pridá všetky parking machines naraz (bez renderov medzi tým)
export const addParkingMachinesBatch = (machinesList: any[]) => {
    const format = new GeoJSON();
    const allFeatures: Feature<Geometry>[] = [];

    machinesList.forEach((properties) => {
        if (parkingMachines.getSource()?.getFeatureById(properties.parkMachineID)) {
            return;
        }

        const geojson = JSON.parse(properties.position);
        const features = format.readFeatures(geojson, {
            featureProjection: "EPSG:5514",
        });

        features.forEach((feature) => {
            if (feature instanceof Feature) {
                feature.setProperties({
                    ...properties,
                    streetName: properties?.streetName?.toUpperCase(),
                    featureType: "parkingMachine",
                    visible: false,
                });
                feature.setStyle(invisibleStyle);
                feature.setId(properties.parkMachineID);
                allFeatures.push(feature as Feature<Geometry>);
            }
        });
    });

    if (allFeatures.length > 0) {
        parkingMachines.getSource()?.addFeatures(allFeatures);
    }
};

export const addMapPin = (geojson: any) => {
    const format = new GeoJSON();
    const features = format.readFeatures(geojson, {
        featureProjection: "EPSG:5514",
    });

    features.forEach((feature) => {
        if (feature instanceof Feature) {
            const iconStyle = new Style({
                image: new Icon({
                    src: "/images/map-pin.svg",
                    scale: 1, // Upravit velikost podle potřeby
                    anchor: [0.5, 0.5], // Ukotvení ikony, aby byla správně umístěná
                }),
            });

            feature.setStyle(iconStyle);
            feature.setProperties({
                featureType: "mapPin",
            });
            feature.setId("mapPin");

            locationMarker.getSource()?.addFeature(feature as Feature<Geometry>);
        }
    });
};

export const addArcgisPathsToMap = (paths: [number, number][][], id: string, color: string = "#FF0000") => {
    if (!paths?.length) return;

    // Podpora vícenásobných čar
    const geometry = paths.length === 1 ? new LineString(paths[0]) : new MultiLineString(paths);

    const feature = new Feature<Geometry>(geometry);

    feature.setId(id);
    feature.setProperties({
        featureType: "arcgisLine",
        visible: true,
    });

    feature.setStyle(
        new Style({
            stroke: new Stroke({
                color: color,
                width: 4,
            }),
        })
    );

    locationMarker.getSource()?.addFeature(feature);
};

export const visibleZonesInViewport = () => {
    let features: Array<Object> = [];
    const extent = map.getView().calculateExtent(map.getSize());

    parkingZones.getSource().forEachFeatureInExtent(extent, function (feature) {
        let properties = feature.getProperties();
        if (properties.visible === true) features.push(properties);
    });
    parkingMachines.getSource().forEachFeatureInExtent(extent, function (feature) {
        let properties = feature.getProperties();
        if (properties.visible === true) features.push(properties);
    });
    return features;
};

export const checkMapWidthLimit = () => {
    const extent = map.getView().calculateExtent(map.getSize());
    const extentInDegrees = transformExtent(extent, "EPSG:5514", "EPSG:4326");
    const widthInDegrees = extentInDegrees[2] - extentInDegrees[0];
    // Server limit je 0.05 degrees, nastavujeme o trochu menej pre buffer
    const limit = 0.048;
    return widthInDegrees <= limit;
};

export const mapDebounce = <F extends (...args: any[]) => void>(func: F, wait: number): ((...args: Parameters<F>) => void) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<F>) => {
        const later = () => {
            timeoutId = null;
            func(...args);
        };

        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(later, wait);
    };
};
